<template>
  <v-dialog v-model="dialog" max-width="760" persistent>
    <v-card rounded="lg" class="cdlg">
      <div class="cdlg__head">
        <div class="cdlg__eyebrow">{{ $t("includeLesson.eyebrow") }}</div>
        <div class="cdlg__title">{{ $t("includeLesson.title") }}</div>
        <div class="cdlg__sub">
          {{ $t("includeLesson.sub") }}
        </div>
      </div>

      <v-card-text class="cdlg__body">
        <v-form ref="form" v-model="valid">
          <v-select
            v-model="form.classId"
            :items="groupOptions"
            :label="$t('includeLesson.class')"
            :rules="[(v) => !!v || $t('includeLesson.selectClass')]"
            color="#A826E1"
            item-color="#A826E1"
            outlined
            dense
            @change="onGroupChange"
          />

          <v-select
            v-model="form.customerIds"
            :items="studentOptions"
            :label="$t('includeLesson.students')"
            :rules="[
              (v) => (v && v.length > 0) || $t('includeLesson.selectStudent'),
            ]"
            multiple
            chips
            small-chips
            color="#A826E1"
            item-color="#A826E1"
            outlined
            dense
            :disabled="!form.classId"
          />

          <div class="cdlg__picker-field">
            <div class="cdlg__picker-label">
              {{ $t("includeLesson.dateTime") }}
            </div>
            <date-picker
              v-if="pickerSlots.length"
              :key="pickerKey"
              ref="picker"
              :horarios="pickerSlots"
            />
            <p v-else class="cdlg__picker-empty">
              {{ $t("includeLesson.noAvailability") }}
            </p>
          </div>

          <v-select
            v-model="form.level"
            :items="levelOptions"
            :label="$t('includeLesson.level')"
            :rules="[(v) => v !== null || $t('includeLesson.selectLevel')]"
            :disabled="!form.classId"
            color="#A826E1"
            item-color="#A826E1"
            outlined
            dense
            @change="loadSubjects"
          />

          <v-select
            v-model="form.courseClassId"
            :items="subjectOptions"
            :label="$t('includeLesson.subject')"
            :rules="[(v) => !!v || $t('includeLesson.selectSubject')]"
            :loading="loadingSubjects"
            :disabled="form.level === null || loadingSubjects"
            color="#A826E1"
            item-color="#A826E1"
            :no-data-text="$t('includeLesson.noSubjects')"
            outlined
            dense
          >
            <template #item="{ item }">
              <div class="subject-option">
                <span class="subject-option__index">{{ item.position }}</span>
                <span class="subject-option__title">{{ item.text }}</span>
                <LessonTagChips
                  :category="item.category"
                  :pre="item.pre"
                  :optional="item.optional"
                />
              </div>
            </template>
          </v-select>
        </v-form>
      </v-card-text>

      <div class="cdlg__actions">
        <v-btn
          text
          class="cdlg__btn"
          color="var(--dk-ink, #231A2E)"
          :disabled="saving"
          @click="close"
        >
          {{ $t("common.cancel") }}
        </v-btn>
        <v-btn
          depressed
          rounded
          class="cdlg__btn"
          color="#A826E1"
          dark
          :loading="saving"
          @click="submit"
        >
          {{ $t("includeLesson.submit") }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";
import {
  ENGLISH_LEVELS,
  groupLabelByStudents,
} from "@/shared/classPresentation";
import LessonTagChips from "@/components/connect/course/LessonTagChips.vue";
import { lessonPosition } from "@/shared/courseTiers";

export default {
  name: "IncluirAulaDialog",
  components: { LessonTagChips },
  props: {
    groups: {
      type: Array,
      default: () => [],
    },
    availability: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      dialog: false,
      valid: false,
      saving: false,
      loadingSubjects: false,
      subjects: [],
      pickerKey: 0,
      form: {
        classId: null,
        customerIds: [],
        courseClassId: null,
        level: null,
      },
    };
  },
  computed: {
    // Card 86akng735: o professor nao identifica a turma pelo codigo. A opcao
    // e nomeada pelos alunos, igual a tela de anotacoes compartilhadas.
    groupOptions() {
      return this.groups.map((group) => ({
        text: groupLabelByStudents(group),
        value: group.id,
      }));
    },
    selectedGroup() {
      return (
        this.groups.find((group) => group.id === this.form.classId) || null
      );
    },
    studentOptions() {
      if (!this.selectedGroup) return [];
      return (this.selectedGroup.content || []).map((student) => ({
        text: student.name,
        value: student.id,
      }));
    },
    pickerSlots() {
      // Alimenta o date-picker com a disponibilidade recorrente do professor.
      // Sem `date`, o picker libera no calendario qualquer data futura cujo dia
      // da semana esteja na disponibilidade e lista os horarios daquele dia.
      return this.availability.map((slot) => ({
        weekDay: slot.weekDay,
        hour: slot.from,
        to: slot.to,
      }));
    },
    subjectOptions() {
      // Sequência do módulo, numerada (card 86ajw35m1).
      return this.subjects.map((subject, index) => ({
        text: subject.title,
        value: subject.id,
        position: lessonPosition(index),
        category: subject.category ?? null,
        pre: !!subject.pre,
        optional: !!subject.optional,
      }));
    },
    levelOptions() {
      return ENGLISH_LEVELS;
    },
  },
  methods: {
    open() {
      this.reset();
      this.dialog = true;
    },
    close() {
      this.dialog = false;
    },
    reset() {
      this.subjects = [];
      // Remonta o date-picker para limpar a data/horario da abertura anterior.
      this.pickerKey += 1;
      this.form = {
        classId: null,
        customerIds: [],
        courseClassId: null,
        level: null,
      };
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    onGroupChange() {
      this.form.customerIds = [];
      this.form.courseClassId = null;
      this.form.level = this.selectedGroup ? this.selectedGroup.level : null;
      this.loadSubjects();
    },
    loadSubjects() {
      this.form.courseClassId = null;
      if (this.form.level === null || this.form.level === undefined) {
        this.subjects = [];
        return;
      }
      this.loadingSubjects = true;
      partnerApi
        .getClassContentByLevel(this.form.level)
        .then((res) => {
          this.subjects = res.data.content || [];
        })
        .catch(() => {
          this.subjects = [];
        })
        .finally(() => {
          this.loadingSubjects = false;
        });
    },
    submit() {
      const picked = this.$refs.picker ? this.$refs.picker.getData() : null;
      if (!picked || !picked.selectedDate || !picked.selectedHour) {
        this.$root.$emit("display-alert", {
          text: this.$t("includeLesson.pickDateTime"),
          type: 1,
        });
        return;
      }
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      partnerApi
        .createComplementaryClass({
          classId: this.form.classId,
          customerIds: this.form.customerIds,
          date: picked.selectedDate,
          weekDay: Number(picked.selectedHour.weekDay),
          startHour: picked.selectedHour.hour,
          endHour: picked.selectedHour.to,
          courseClassId: this.form.courseClassId,
        })
        .then(() => {
          this.$root.$emit("display-alert", {
            text: this.$t("includeLesson.created"),
            type: 0,
          });
          this.dialog = false;
          this.$emit("created");
        })
        .catch((error) => {
          const text =
            error?.response?.data?.message ||
            this.$t("includeLesson.createFailed");
          this.$root.$emit("display-alert", { text, type: 1 });
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.subject-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
}
.subject-option__index {
  flex: none;
  min-width: 20px;
  color: var(--dk-faint, #9a96a6);
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
}
.subject-option__title {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cdlg {
  --accent: var(--dk-grape, #a826e1);
  border-radius: 20px !important;
  color: var(--dk-ink, #231a2e);
}
.cdlg__head {
  padding: 22px 24px 4px;
}
.cdlg__eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent);
}
.cdlg__title {
  margin-top: 4px;
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.2;
  color: var(--dk-ink, #231a2e);
}
.cdlg__sub {
  margin-top: 6px;
  font-size: 0.88rem;
  color: var(--dk-muted, #6b6472);
  line-height: 1.5;
}
.cdlg__body {
  padding-top: 14px;
  color: var(--dk-ink, #231a2e);
}
.cdlg__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 6px 20px 18px;
}
.cdlg__btn {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 700;
}
.cdlg__picker-field {
  margin-bottom: 8px;
  border: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.14);
  border-radius: 8px;
  padding: 8px 12px 4px;
}
.cdlg__picker-label {
  font-size: 0.78rem;
  color: var(--dk-muted, #6b6472);
  font-weight: 600;
}
.cdlg__picker-empty {
  margin: 8px 0;
  font-size: 0.85rem;
  color: var(--dk-muted, #6b6472);
}
</style>
